import React, { useCallback } from "react"
import { useTranslation } from "react-i18next"

import classNames from "classnames"

import { useRoomTypeOptions } from "shared/lib/room-types/useRoomTypeOptions"
import { SelectOption } from "shared/types/Select"
import { WithClassName } from "shared/types/WithClassName"
import { RoomType } from "shared/types/references/RoomType"
import { Icon } from "shared/ui/icon/Icon"
import { IconName } from "shared/ui/icon/__generated__/IconName"
import { Combobox } from "shared/ui/input/combobox/Combobox"

import { useRoomFormContext } from "../../contexts/RoomFormContext"

export interface RoomUtilizationInputProps extends WithClassName {
  hideLabel?: boolean
}

export const RoomUtilizationInput = React.memo(
  ({ className, hideLabel }: RoomUtilizationInputProps) => {
    const { t } = useTranslation()
    const { isReadonly, isDisabled } = useRoomFormContext()
    const options = useRoomTypeOptions()

    const renderValue = useCallback(
      (value: SelectOption<RoomType> | null) =>
        value ? (
          <span>{value.label}</span>
        ) : (
          <span className="combobox__placeholder">{t("General.Select")}</span>
        ),
      [t]
    )

    return (
      <Combobox.Root
        name="room.utilization"
        label={t("Room.utilization")}
        className={classNames("room-form__utilization", className)}
        readonly={isReadonly}
        disabled={isDisabled}
        data-testid="room__utilization__combobox"
        items={options}
      >
        {!hideLabel && <Combobox.Label />}
        {!isReadonly && (
          <Combobox.Control>
            <Combobox.Trigger>
              <Combobox.Value>{renderValue}</Combobox.Value>
              <Combobox.Icon />
            </Combobox.Trigger>
            <Combobox.Portal>
              <Combobox.Positioner align="start" sideOffset={8}>
                <Combobox.Popup aria-label={t("Room.utilization")}>
                  <Combobox.InputContainer>
                    <Combobox.Input />
                    <Icon name={IconName.SEARCH} />
                  </Combobox.InputContainer>
                  <Combobox.Empty />
                  <Combobox.List>
                    {(option: SelectOption<RoomType>) => (
                      <Combobox.Item key={option.value} value={option}>
                        <Combobox.ItemIndicator />
                        <Combobox.ItemContent>
                          {option.label}
                        </Combobox.ItemContent>
                      </Combobox.Item>
                    )}
                  </Combobox.List>
                </Combobox.Popup>
              </Combobox.Positioner>
            </Combobox.Portal>
          </Combobox.Control>
        )}
        <Combobox.Readonly items={options} />
        <Combobox.ErrorMessage />
      </Combobox.Root>
    )
  }
)
